/**
 * PEStart Page — Neugründung / Übernahme Wizard + Dashboard + Text-Engine.
 * Isoliertes Premium-Modul; keine Stammkunden-Mutation.
 * Einstieg: Archiv-Übersicht → Neu (Wizard) oder bestehenden Plan öffnen.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useShallow } from 'zustand/react/shallow';
import { functions } from '../config/firebase';
import useAppStore from '../store/appStore';
import { useCalculationsSelector } from '../store/selectors/calculations';
import usePEStartStore, {
    createDefaultPEStartState,
    normalizeGeneratedSections,
} from '../store/usePEStartStore';
import PEStartWizardStep1 from '../components/peStart/PEStartWizardStep1';
import PEStartWizardStep2 from '../components/peStart/PEStartWizardStep2';
import PEStartDashboard from '../components/peStart/PEStartDashboard';
import PEStartAufbauPlan from '../components/peStart/PEStartAufbauPlan';
import PEStartTextEngine, {
    type BusinessPlanContext,
    type GeneratedSectionText,
} from '../components/peStart/PEStartTextEngine';
import PEStartArchivPanel from '../components/peStart/PEStartArchivPanel';
import PEStartKfWProfilPanel from '../components/peStart/PEStartKfWProfilPanel';
import { computePEStartDashboard } from '../components/peStart/peStartCalcAdapter';
import { computePEStartAufbauPlan } from '../components/peStart/peStartPlanungsBridge';
import {
    buildPEStartArchivDeletePatch,
    createPEStartArchivEintrag,
    foundingTypeLabel,
    normalizePEStartArchiv,
    type PEStartArchivEintrag,
    type PEStartKennzahlSnapshot,
} from '../components/peStart/peStartArchivModel';

type PEStartTab = 'wizard' | 'dashboard' | 'aufbau' | 'text' | 'archiv';
type PEStartView = 'hub' | 'workspace';

const generatePeStartBusinessPlan = httpsCallable(functions, 'generatePeStartBusinessPlan');

function resolveEntryGeneratedSections(
    entry: PEStartArchivEintrag,
    textsMap: Record<string, unknown> | null | undefined
): ReturnType<typeof normalizeGeneratedSections> {
    const fromState = normalizeGeneratedSections(
        (entry.state as any)?.generatedSections
    );
    if (fromState.length > 0) return fromState;
    if (textsMap && typeof textsMap === 'object') {
        return normalizeGeneratedSections(textsMap[entry.id]);
    }
    return [];
}

function kiTextStatusLabel(hasText: boolean): string {
    return hasText ? 'KI-Text vorhanden' : 'Kein KI-Text';
}

const PEStartPage: React.FC = () => {
    const [view, setView] = useState<PEStartView>('hub');
    const [tab, setTab] = useState<PEStartTab>('wizard');
    const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1);

    // Hub: Neuanlage & Umbenennen
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newPlanTitel, setNewPlanTitel] = useState('');
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');

    // Workspace: Plan-Titel & Speicher-Feedback
    const [workspaceTitel, setWorkspaceTitel] = useState('');
    const [saveToast, setSaveToast] = useState<string | null>(null);

    const calc = useCalculationsSelector() as any;
    const privateCostData = useAppStore((s) => s.clientData?.privateCostData);
    const clientData = useAppStore((s) => s.clientData);
    const settings = useAppStore((s) => s.settings);
    const setClientData = useAppStore((s) => s.setClientData);
    const openPremiumModal = useAppStore((s) => s.openPremiumModal);

    const appSlice = useAppStore(
        useShallow((s) => ({
            planungsAnalysePrognose: s.planungsAnalysePrognose,
            planungsAnalyseArchiv: s.planungsAnalyseArchiv,
            planungsAnalyseActiveSzenarioId: s.planungsAnalyseActiveSzenarioId,
            planungsAnalyseBasisDatenTyp: s.planungsAnalyseBasisDatenTyp,
            peProfitDataVersion: s.peProfitDataVersion,
            clientData: s.clientData,
        }))
    );

    const peState = usePEStartStore(
        useShallow((s) => ({
            foundingType: s.foundingType,
            subsidies: s.subsidies,
            investments: s.investments,
            staffFlags: s.staffFlags,
            loanPlan: s.loanPlan,
            selectedPlanungsrechnerSzenarioId: s.selectedPlanungsrechnerSzenarioId,
            generatedSections: s.generatedSections,
            privateAltersvorsorge: s.privateAltersvorsorge,
            liquiditaetspufferMonat: s.liquiditaetspufferMonat,
            fachlicheQualifikation: s.fachlicheQualifikation,
            berufserfahrung: s.berufserfahrung,
            kaufmaennischeEignung: s.kaufmaennischeEignung,
            executiveSummaryNotes: s.executiveSummaryNotes,
        }))
    );

    const startNewBusinessPlan = usePEStartStore((s) => s.startNewBusinessPlan);
    const openArchivEntry = usePEStartStore((s) => s.openArchivEntry);
    const setActiveArchivId = usePEStartStore((s) => s.setActiveArchivId);
    const activeArchivId = usePEStartStore((s) => s.activeArchivId);

    const archiv = useMemo(
        () => normalizePEStartArchiv(clientData?.peStartArchiv),
        [clientData?.peStartArchiv]
    );

    const textsMap = useMemo(() => {
        const raw = (clientData as any)?.peStartGeneratedTexts;
        return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    }, [clientData]);

    const salonLabel = useMemo(() => {
        const raw =
            clientData?.firmenname ||
            clientData?.salonname ||
            clientData?.unternehmen ||
            '';
        return String(raw).trim();
    }, [clientData?.firmenname, clientData?.salonname, clientData?.unternehmen]);

    const activeEntry = useMemo(
        () => archiv.find((e) => e.id === activeArchivId) || null,
        [archiv, activeArchivId]
    );

    useEffect(() => {
        if (activeEntry?.titel) {
            setWorkspaceTitel(activeEntry.titel);
        }
    }, [activeEntry?.id, activeEntry?.titel]);

    const aufbau = useMemo(
        () =>
            computePEStartAufbauPlan(
                appSlice,
                calc,
                clientData,
                settings,
                peState.selectedPlanungsrechnerSzenarioId
            ),
        [appSlice, calc, clientData, settings, peState.selectedPlanungsrechnerSzenarioId]
    );
    const verbesserungDelta = aufbau.available ? aufbau.summary?.umsatzDeltaMonat ?? 0 : 0;

    const liveModel = useMemo(
        () => computePEStartDashboard(peState, calc, privateCostData, verbesserungDelta),
        [peState, calc, privateCostData, verbesserungDelta]
    );

    const buildKennzahlenSnapshot = useCallback(
        (stateForCalc = peState): PEStartKennzahlSnapshot => {
            const model =
                stateForCalc === peState
                    ? liveModel
                    : computePEStartDashboard(
                          stateForCalc,
                          calc,
                          privateCostData,
                          verbesserungDelta
                      );
            return {
                planumsatzNetto: model.kpis.planumsatzNetto,
                totpunktMonatBrutto: model.kpis.totpunktMonatBrutto,
                unternehmerlohn: model.kpis.unternehmerlohnKompass,
                startFinanzbedarf: model.kpis.startFinanzbedarf,
                kreditbetrag: model.kpis.finanzbedarf.kreditbetrag,
                rateMonat: model.kpis.finanzbedarf.rateMonat,
                laufzeitJahre: model.kpis.finanzbedarf.tilgungLaufzeitJahre,
                zinssatzProzent: model.kpis.finanzbedarf.zinssatzProzent,
                pufferMonate: model.kpis.finanzbedarf.anlaufPufferMonate,
                breakEvenMonat: model.breakEvenMonat,
                foundingType: model.foundingType,
                monate: model.months.map((m) => ({
                    month: m.month,
                    umsatzZiel: m.umsatzZiel,
                    umsatzPrognose: m.umsatzPrognose,
                })),
            };
        },
        [peState, liveModel, calc, privateCostData, verbesserungDelta]
    );

    const handleSaveActivePlan = useCallback(
        (silent = false) => {
            if (typeof setClientData !== 'function') return;
            const defaultTitle = `Businessplan ${
                peState.foundingType === 'TAKEOVER' ? 'Übernahme' : 'Neugründung'
            } ${new Date().toLocaleDateString('de-DE')}`;
            const cleanTitle =
                workspaceTitel.trim() || activeEntry?.titel || defaultTitle;
            const kennzahlen = buildKennzahlenSnapshot(peState);

            if (activeArchivId && archiv.some((e) => e.id === activeArchivId)) {
                const updatedList = archiv.map((e) =>
                    e.id === activeArchivId
                        ? {
                              ...e,
                              titel: cleanTitle,
                              timestamp: Date.now(),
                              state: JSON.parse(JSON.stringify(peState)),
                              kennzahlen,
                          }
                        : e
                );
                setClientData((prev: any) => ({
                    ...prev,
                    peStartArchiv: updatedList,
                }));
            } else {
                const created = createPEStartArchivEintrag(
                    cleanTitle,
                    peState,
                    kennzahlen
                );
                setClientData((prev: any) => ({
                    ...prev,
                    peStartArchiv: [created, ...archiv],
                }));
                setActiveArchivId(created.id);
                setWorkspaceTitel(created.titel);
            }

            if (!silent) {
                setSaveToast('Gespeichert');
                setTimeout(() => setSaveToast(null), 2400);
            }
        },
        [
            setClientData,
            peState,
            workspaceTitel,
            activeEntry?.titel,
            buildKennzahlenSnapshot,
            activeArchivId,
            archiv,
            setActiveArchivId,
        ]
    );

    const handleGenerateText = useCallback(
        async (context: BusinessPlanContext): Promise<GeneratedSectionText[] | string> => {
            const response = await generatePeStartBusinessPlan({ context });
            const data = response?.data as any;

            if (Array.isArray(data)) {
                return data as GeneratedSectionText[];
            }
            if (Array.isArray(data?.sections)) {
                return data.sections as GeneratedSectionText[];
            }
            if (typeof data?.text === 'string') {
                return data.text;
            }
            throw new Error('Unerwartete Antwort von generatePeStartBusinessPlan.');
        },
        []
    );

    const goHub = () => {
        setView('hub');
    };

    const handleStartCreatePlan = () => {
        const freshState = createDefaultPEStartState();
        startNewBusinessPlan();
        const title =
            newPlanTitel.trim() ||
            `Businessplan ${new Date().toLocaleDateString('de-DE')}`;
        const entry = createPEStartArchivEintrag(
            title,
            freshState,
            buildKennzahlenSnapshot(freshState),
            undefined,
            'entwurf'
        );
        if (typeof setClientData === 'function') {
            setClientData((prev: any) => ({
                ...prev,
                peStartArchiv: [entry, ...archiv],
            }));
        }
        setActiveArchivId(entry.id);
        setWorkspaceTitel(entry.titel);
        setNewPlanTitel('');
        setShowCreateForm(false);
        setWizardStep(1);
        setTab('wizard');
        setView('workspace');
    };

    const handleOpenEntry = (
        entry: PEStartArchivEintrag,
        targetTab?: PEStartTab
    ) => {
        const sections = resolveEntryGeneratedSections(entry, textsMap);
        openArchivEntry(entry.id, {
            ...entry.state,
            generatedSections: sections,
        });
        setWorkspaceTitel(entry.titel);
        setWizardStep(1);
        setTab(targetTab || 'wizard');
        setView('workspace');
    };

    const handleRenameEntry = (id: string) => {
        const clean = renameValue.trim();
        if (!clean || typeof setClientData !== 'function') {
            setRenamingId(null);
            return;
        }
        const nextArchiv = archiv.map((e) =>
            e.id === id ? { ...e, titel: clean, timestamp: Date.now() } : e
        );
        setClientData((prev: any) => ({
            ...prev,
            peStartArchiv: nextArchiv,
        }));
        if (activeArchivId === id) {
            setWorkspaceTitel(clean);
        }
        setRenamingId(null);
        setRenameValue('');
    };

    const handleDeleteEntry = (entry: PEStartArchivEintrag, e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        const label = entry.titel?.trim() || 'diesen Businessplan';
        if (!window.confirm(`„${label}“ wirklich unwiderruflich löschen?`)) return;
        if (typeof setClientData !== 'function') return;
        setClientData((prev: any) => ({
            ...prev,
            ...buildPEStartArchivDeletePatch(prev, entry.id),
        }));
        if (activeArchivId === entry.id) {
            setActiveArchivId(null);
            setView('hub');
        }
    };

    return (
        <div className="min-h-screen bg-pe-bg dark:bg-[#030303] px-4 py-6 md:px-8">
            <header className="mb-6 max-w-5xl mx-auto">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                            Premium · Analyse
                        </p>
                        <h1 className="text-2xl font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]">
                            <span className="italic font-normal">PE</span>Start
                        </h1>
                        <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                            {view === 'hub'
                                ? 'Gespeicherte Businesspläne öffnen, verwalten oder einen neuen Plan anlegen.'
                                : 'Schritt für Schritt: Eingaben erfassen → Anlauf-Dashboard prüfen → Businessplan & PDF erstellen.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => openPremiumModal('pe-start')}
                        className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/20"
                    >
                        Tarifübersicht
                    </button>
                </div>

                {view === 'workspace' && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] px-4 py-3">
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                            <button
                                type="button"
                                onClick={goHub}
                                className="rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] px-3 py-2 text-xs font-bold uppercase tracking-wider text-pe-muted dark:text-slate-300 hover:border-[#E6C299]/40 hover:text-[#E6C299]"
                            >
                                ← Übersicht
                            </button>
                            <div className="flex min-w-[200px] flex-1 items-center gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                    Plan:
                                </span>
                                <input
                                    type="text"
                                    value={workspaceTitel}
                                    onChange={(e) => setWorkspaceTitel(e.target.value)}
                                    placeholder="Name des Businessplans"
                                    className="w-full max-w-sm rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] px-3 py-1.5 text-sm font-semibold text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            {saveToast && (
                                <span className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-400">
                                    ✓ {saveToast}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={() => handleSaveActivePlan(false)}
                                className="rounded-lg border border-[#E6C299]/50 bg-[#E6C299] px-4 py-2 text-xs font-black uppercase tracking-wider text-black hover:bg-[#f0d4b0]"
                            >
                                Stand speichern
                            </button>
                        </div>
                    </div>
                )}

                {view === 'workspace' && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {(
                            [
                                { id: 'wizard' as const, label: '1 · Eingabe-Wizard' },
                                { id: 'dashboard' as const, label: '2 · Anlauf-Dashboard' },
                                { id: 'aufbau' as const, label: '3 · Aufbau-Plan' },
                                { id: 'text' as const, label: '4 · Businessplan & PDF' },
                                { id: 'archiv' as const, label: '5 · Plan ↔ IST-Check' },
                            ] as const
                        ).map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTab(t.id)}
                                className={`rounded-lg border px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                                    tab === t.id
                                        ? 'border-[#E6C299] bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                        : 'border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] text-pe-muted dark:text-slate-400 hover:border-pe-border-strong dark:hover:border-[#444] hover:text-pe-text dark:hover:text-white'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                )}
            </header>

            <div className="mx-auto max-w-5xl space-y-4">
                {view === 'hub' && (
                    <section className="rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] p-5">
                        <div className="mb-5 flex flex-wrap items-start justify-between gap-3 border-b border-pe-border dark:border-[#222] pb-4">
                            <div>
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                                    PEStart · Übersicht
                                </p>
                                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                                    Gespeicherte Businesspläne
                                </h2>
                                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                                    {archiv.length} Eintrag{archiv.length === 1 ? '' : 'e'}
                                    {salonLabel ? ` · ${salonLabel}` : ''}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCreateForm((v) => !v)}
                                className="rounded-lg border border-[#E6C299]/50 bg-[#E6C299] px-4 py-3 text-sm font-black uppercase tracking-wider text-black hover:bg-[#f0d4b0]"
                            >
                                + Neuen Businessplan anlegen
                            </button>
                        </div>

                        {showCreateForm && (
                            <div className="mb-5 rounded-xl border border-[#E6C299]/40 bg-pe-elevated dark:bg-[#141414] p-4">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]">
                                    Neuen Businessplan starten
                                </p>
                                <div className="flex flex-wrap items-center gap-2">
                                    <input
                                        type="text"
                                        value={newPlanTitel}
                                        onChange={(e) => setNewPlanTitel(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleStartCreatePlan();
                                        }}
                                        placeholder="z. B. Übernahme Salon Müller / KfW-Antrag 2026"
                                        autoFocus
                                        className="min-w-[240px] flex-1 rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2 text-sm text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleStartCreatePlan}
                                        className="rounded-lg border border-[#E6C299]/50 bg-[#E6C299] px-4 py-2 text-xs font-black uppercase tracking-wider text-black hover:bg-[#f0d4b0]"
                                    >
                                        Anlegen &amp; starten →
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setNewPlanTitel('');
                                        }}
                                        className="rounded-lg border border-pe-border-strong dark:border-[#333] px-3 py-2 text-xs font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400"
                                    >
                                        Abbrechen
                                    </button>
                                </div>
                            </div>
                        )}

                        {archiv.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] px-4 py-10 text-center">
                                <p className="text-sm text-pe-muted dark:text-slate-400">
                                    Noch keine Businesspläne gespeichert.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateForm(true)}
                                    className="mt-4 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:underline"
                                >
                                    Jetzt ersten Plan anlegen
                                </button>
                            </div>
                        ) : (
                            <ul className="space-y-2">
                                {archiv.map((entry) => {
                                    const hasKi =
                                        resolveEntryGeneratedSections(entry, textsMap).length > 0;
                                    const isRenaming = renamingId === entry.id;
                                    return (
                                        <li
                                            key={entry.id}
                                            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3.5 transition-colors hover:border-[#E6C299]/40"
                                        >
                                            <div className="min-w-0 flex-1">
                                                {isRenaming ? (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <input
                                                            type="text"
                                                            value={renameValue}
                                                            onChange={(e) =>
                                                                setRenameValue(e.target.value)
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter')
                                                                    handleRenameEntry(entry.id);
                                                                if (e.key === 'Escape')
                                                                    setRenamingId(null);
                                                            }}
                                                            autoFocus
                                                            className="rounded-lg border border-[#E6C299]/50 bg-pe-card dark:bg-[#0f0f0f] px-3 py-1.5 text-sm text-pe-text dark:text-white outline-none"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                handleRenameEntry(entry.id)
                                                            }
                                                            className="rounded-lg bg-[#E6C299] px-3 py-1.5 text-[10px] font-black uppercase text-black"
                                                        >
                                                            OK
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setRenamingId(null)}
                                                            className="rounded-lg border border-pe-border-strong dark:border-[#333] px-2.5 py-1.5 text-[10px] font-bold uppercase text-pe-muted dark:text-slate-400"
                                                        >
                                                            Abbruch
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenEntry(entry)}
                                                        className="w-full text-left"
                                                    >
                                                        <p className="text-sm font-semibold text-pe-text dark:text-white">
                                                            {entry.titel}
                                                            {salonLabel ? (
                                                                <span className="font-normal text-stone-500 dark:text-slate-500">
                                                                    {' '}
                                                                    · {salonLabel}
                                                                </span>
                                                            ) : null}
                                                        </p>
                                                        <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                                                            {foundingTypeLabel(
                                                                entry.kennzahlen?.foundingType ||
                                                                    entry.state?.foundingType
                                                            )}
                                                            {' · '}
                                                            {new Date(
                                                                entry.timestamp
                                                            ).toLocaleString('de-DE')}
                                                            {entry.kennzahlen?.kreditbetrag > 0 && (
                                                                <>
                                                                    {' · '}Kredit{' '}
                                                                    {liveModel.formatCurrency(
                                                                        entry.kennzahlen
                                                                            .kreditbetrag,
                                                                        0
                                                                    )}
                                                                </>
                                                            )}
                                                        </p>
                                                        {entry.note ? (
                                                            <p className="mt-1 truncate text-xs text-slate-600">
                                                                {entry.note}
                                                            </p>
                                                        ) : null}
                                                    </button>
                                                )}
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2">
                                                <span
                                                    className={`rounded-md border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                                                        hasKi
                                                            ? 'border-emerald-600 dark:border-emerald-500/40 bg-emerald-100 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-300'
                                                            : 'border-pe-border-strong dark:border-[#333] bg-pe-card dark:bg-[#1a1a1a] text-stone-600 dark:text-slate-500'
                                                    }`}
                                                >
                                                    {kiTextStatusLabel(hasKi)}
                                                </span>

                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenEntry(entry, 'wizard')}
                                                    className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25"
                                                >
                                                    Öffnen
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setRenamingId(entry.id);
                                                        setRenameValue(entry.titel);
                                                    }}
                                                    className="rounded-lg border border-pe-border-strong dark:border-[#333] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-pe-muted dark:text-slate-300 hover:border-[#E6C299]/40"
                                                >
                                                    Umbenennen
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleDeleteEntry(entry, e)}
                                                    className="rounded-lg border border-red-500/30 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-red-400/90 hover:bg-red-950/30"
                                                    title="Businessplan löschen"
                                                >
                                                    Löschen
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                )}

                {view === 'workspace' && (
                    <>
                        {tab === 'wizard' && (
                            <>
                                <div className="flex flex-wrap gap-2">
                                    {(
                                        [
                                            {
                                                step: 1 as const,
                                                label: 'Schritt 1 · Gründungstyp & Förderung',
                                            },
                                            {
                                                step: 2 as const,
                                                label: 'Schritt 2 · Investitionen, Kredit & Team',
                                            },
                                            {
                                                step: 3 as const,
                                                label: 'Schritt 3 · KfW-Profil & Vorhaben',
                                            },
                                        ] as const
                                    ).map((s) => (
                                        <button
                                            key={s.step}
                                            type="button"
                                            onClick={() => setWizardStep(s.step)}
                                            className={`rounded-lg px-3.5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                                                wizardStep === s.step
                                                    ? 'bg-[#E6C299] text-black'
                                                    : 'border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white'
                                            }`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>

                                {wizardStep === 1 && <PEStartWizardStep1 />}
                                {wizardStep === 2 && <PEStartWizardStep2 />}
                                {wizardStep === 3 && <PEStartKfWProfilPanel />}

                                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] px-4 py-3">
                                    <div>
                                        {wizardStep > 1 && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setWizardStep((prev) =>
                                                        prev === 3 ? 2 : 1
                                                    )
                                                }
                                                className="rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] px-4 py-2 text-xs font-bold uppercase tracking-wider text-pe-muted dark:text-slate-300 hover:border-[#E6C299]/40"
                                            >
                                                ← Zurück zu Schritt {wizardStep - 1}
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {wizardStep < 3 ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleSaveActivePlan(true);
                                                    setWizardStep((prev) =>
                                                        prev === 1 ? 2 : 3
                                                    );
                                                }}
                                                className="rounded-lg border border-[#E6C299]/50 bg-[#E6C299] px-4 py-2.5 text-xs font-black uppercase tracking-wider text-black hover:bg-[#f0d4b0]"
                                            >
                                                Weiter zu Schritt {wizardStep + 1} →
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleSaveActivePlan(false);
                                                    setTab('dashboard');
                                                }}
                                                className="rounded-lg border border-[#E6C299]/50 bg-[#E6C299] px-4 py-2.5 text-xs font-black uppercase tracking-wider text-black hover:bg-[#f0d4b0]"
                                            >
                                                Speichern &amp; zum Anlauf-Dashboard →
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {tab === 'dashboard' && <PEStartDashboard />}
                        {tab === 'aufbau' && <PEStartAufbauPlan />}
                        {tab === 'text' && (
                            <PEStartTextEngine onGenerateText={handleGenerateText} />
                        )}
                        {tab === 'archiv' && (
                            <PEStartArchivPanel
                                onGoToTextEngine={() => setTab('text')}
                                onOpenForEdit={(entry) => {
                                    const sections = resolveEntryGeneratedSections(
                                        entry,
                                        textsMap
                                    );
                                    openArchivEntry(entry.id, {
                                        ...entry.state,
                                        generatedSections: sections,
                                    });
                                    setWorkspaceTitel(entry.titel);
                                    setTab('wizard');
                                    setWizardStep(1);
                                }}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PEStartPage;
